import React, { useEffect, useMemo, useState, Component } from 'react';
import { getTenant } from '../../theme/tokens';
import { useTranslation } from 'react-i18next';
import { computeTransactionViewModel, assertConsistency, getRowNetImpact, type LedgerRow, type Transaction } from '../../features/payments/utils/transactionViewModel';
import { buildScenario, DEFAULT_SCENARIO_ID, type TransactionScenario } from '../../features/payments/utils/transactionScenarios';
interface Badge {
  tone?: 'emerald' | 'amber' | 'red' | 'slate' | 'blue' | 'violet';
  children: React.ReactNode;
}
function Badge({
  tone = 'slate',
  children
}: Badge) {
  const tones = {
    emerald: 'bg-[#FFFBE6] text-[#B38A00] ring-[#FFE05C]',
    amber: 'bg-amber-50 text-amber-700 ring-amber-200',
    red: 'bg-red-50 text-red-700 ring-red-200',
    slate: 'bg-slate-50 text-slate-700 ring-slate-200',
    blue: 'bg-blue-50 text-blue-700 ring-blue-200',
    violet: 'bg-violet-50 text-violet-700 ring-violet-200'
  };
  return <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${tones[tone] || tones.slate}`} data-id="element-13915">
      {children}
    </span>;
}
interface MoneyProps {
  value: number;
  currency?: string;
}
function Money({
  value,
  currency = getTenant().currency.code
}: MoneyProps) {
  const abs = Math.abs(value);
  const formatted = `${abs.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })} ${currency}`;
  return <span className="text-slate-900" data-id="element-13916">
      {value < 0 ? `−${formatted}` : formatted}
    </span>;
}
function DeltaMoney({
  value,
  currency = getTenant().currency.code
}: MoneyProps) {
  const abs = Math.abs(value);
  const formatted = `${abs.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })} ${currency}`;
  const isNeg = value < 0;
  return <span className={isNeg ? 'text-red-700' : 'text-[#B38A00]'} data-id="element-13917">
      {isNeg ? `−${formatted}` : `+${formatted}`}
    </span>;
}
interface CardProps {
  title?: string;
  subtitle?: string;
  right?: React.ReactNode;
  children: React.ReactNode;
}
function Card({
  title,
  subtitle,
  right,
  children
}: CardProps) {
  return <div className="rounded-2xl bg-white shadow-sm ring-1 ring-slate-200" data-id="element-13918">
      {(title || subtitle || right) && <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4" data-id="element-13919">
          <div data-id="element-13920">
            {title && <div className="text-sm font-semibold text-slate-900" data-id="element-13921">
                {title}
              </div>}
            {subtitle && <div className="mt-1 text-xs text-slate-600" data-id="element-13922">{subtitle}</div>}
          </div>
          {right && <div className="shrink-0" data-id="element-13923">{right}</div>}
        </div>}
      <div className="px-5 py-4" data-id="element-13924">{children}</div>
    </div>;
}
interface RowProps {
  label: string;
  value: React.ReactNode;
  hint?: string;
}
function Row({
  label,
  value,
  hint
}: RowProps) {
  return <div className="flex items-start justify-between gap-4 py-2" data-id="element-13925">
      <div data-id="element-13926">
        <div className="text-sm text-slate-700" data-id="element-13927">{label}</div>
        {hint && <div className="mt-0.5 text-xs text-slate-500" data-id="element-13928">{hint}</div>}
      </div>
      <div className="text-sm font-medium text-slate-900" data-id="element-13929">{value}</div>
    </div>;
}
interface TableProps {
  children: React.ReactNode;
}
function Table({
  children
}: TableProps) {
  return <div className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-200" data-id="element-13930">
      {children}
    </div>;
}
interface ThProps {
  children: React.ReactNode;
  align?: 'left' | 'right';
}
function Th({
  children,
  align = 'left'
}: ThProps) {
  return <th className={`px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600 ${align === 'right' ? 'text-right' : 'text-left'}`} data-id="element-13931">
      {children}
    </th>;
}
interface TdProps {
  children: React.ReactNode;
  align?: 'left' | 'right';
}
function Td({
  children,
  align = 'left'
}: TdProps) {
  return <td className={`px-4 py-3 text-sm text-slate-800 ${align === 'right' ? 'text-right' : 'text-left'}`} data-id="element-13932">
      {children}
    </td>;
}
interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}
function Drawer({
  open,
  onClose,
  title,
  children
}: DrawerProps) {
  const {
    t
  } = useTranslation('common');
  if (!open) return null;
  return <div className="fixed inset-0 z-50" data-id="element-13933">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} data-id="element-13934" />
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl overflow-y-auto" data-id="element-13935">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4" data-id="element-13936">
          <div className="text-sm font-semibold text-slate-900" data-id="element-13937">{title}</div>
          <button className="rounded-lg px-2 py-1 text-sm text-slate-600 hover:bg-slate-100" onClick={onClose} data-id="element-13938">
            {t('actions.close')}
          </button>
        </div>
        <div className="px-5 py-4" data-id="element-13939">{children}</div>
      </div>
    </div>;
}
function fmtDate(dateStr: string) {
  return dateStr || '—';
}
interface LedgerRow {
  id: string;
  posting: string;
  attemptId: string | null;
  type: 'payment' | 'fee' | 'refund' | 'dispute' | 'reserve_hold' | 'reserve_release' | 'dispute_reversal';
  gross: number | null;
  feeExTax: number | null;
  taxAmount: number | null;
  status: 'Posted' | 'Pending' | 'Scheduled';
  effectiveAt: string;
  settlement: {
    kind: 'payout' | 'reserve';
    id: string;
    date?: string;
    releaseDate?: string;
    status: string;
    amountInBucket: number;
  } | null;
  feeBreakdown: {
    jurisdiction: string;
    items: Array<{
      label: string;
      amountExTax: number;
      taxAmount: number;
    }>;
  } | null;
}
interface TransactionFinancialBreakdownProps {
  transactionId?: string;
  scenarioId?: string;
  currency?: string;
}
export function TransactionFinancialBreakdown({
  transactionId,
  scenarioId = DEFAULT_SCENARIO_ID,
  currency = getTenant().currency.code
}: TransactionFinancialBreakdownProps) {
  const {
    t
  } = useTranslation('common');
  const [taxDisplay, setTaxDisplay] = useState<'included' | 'separate'>('included');
  const asOf = new Date().toLocaleString('en-GB', {
    dateStyle: 'short',
    timeStyle: 'short'
  });
  // Load scenario data (single source of truth)
  const scenario = useMemo(() => {
    try {
      return buildScenario(scenarioId);
    } catch (error) {
      console.error('Failed to load scenario:', scenarioId, error);
      // Return a fallback minimal scenario
      return {
        scenarioId: 'fallback',
        description: 'Fallback scenario',
        transaction: {
          id: '0',
          transactionId: transactionId || 'TXN-FALLBACK',
          amount: 0,
          currency: currency,
          date: new Date().toISOString()
        },
        ledgerRows: []
      };
    }
  }, [scenarioId, transactionId, currency]);
  const transaction = scenario.transaction;
  const ledger = scenario.ledgerRows || [];
  const [drawer, setDrawer] = useState<{
    open: boolean;
    ledgerId: string | null;
  }>({
    open: false,
    ledgerId: null
  });
  const selected = useMemo(() => ledger.find(x => x.id === drawer.ledgerId) || null, [drawer.ledgerId, ledger]);
  // Compute view model from ledger (single source of truth)
  const viewModel = useMemo(() => {
    try {
      const vm = computeTransactionViewModel(transaction, ledger, taxDisplay);
      // Ensure all required properties exist
      return {
        ...vm,
        timelineItems: vm.timelineItems || [],
        payoutBuckets: vm.payoutBuckets || [],
        reserveBucket: vm.reserveBucket || null,
        summary: vm.summary || {
          grossImpact: 0,
          feesPostedEx: 0,
          feesPendingEx: 0,
          taxPosted: 0,
          taxPending: 0,
          feesPostedDisplay: 0,
          feesPendingDisplay: 0,
          reserveHeld: 0,
          nextReserveRelease: null,
          netPosted: 0,
          paidOut: 0,
          scheduledPayoutTotal: 0
        }
      };
    } catch (error) {
      console.error('Failed to compute view model:', error);
      // Return a safe fallback
      return {
        headerAmount: 0,
        headerState: 'Unknown',
        summary: {
          grossImpact: 0,
          feesPostedEx: 0,
          feesPendingEx: 0,
          taxPosted: 0,
          taxPending: 0,
          feesPostedDisplay: 0,
          feesPendingDisplay: 0,
          reserveHeld: 0,
          nextReserveRelease: null,
          netPosted: 0,
          paidOut: 0,
          scheduledPayoutTotal: 0
        },
        payoutBuckets: [],
        reserveBucket: null,
        timelineItems: []
      };
    }
  }, [transaction, ledger, taxDisplay]);
  // Run consistency checks on mount and when data changes
  useEffect(() => {
    console.log(`\n=== Scenario: ${scenario.scenarioId} ===`);
    console.log(`Description: ${scenario.description}`);
    assertConsistency(viewModel, transaction, ledger);
  }, [viewModel, transaction, ledger, scenario]);
  const computed = viewModel.summary;
  const flags = [];
  if (Math.abs(computed.feesPendingEx) > 0.0001 || Math.abs(computed.taxPending) > 0.0001) flags.push({
    tone: 'amber' as const,
    label: 'Fees pending'
  });
  if (computed.reserveHeld > 0.0001) flags.push({
    tone: 'violet' as const,
    label: 'Reserve active'
  });
  if (viewModel.payoutBuckets.length > 0) flags.push({
    tone: 'blue' as const,
    label: 'Scheduled payouts'
  });
  // Helper functions for ledger table display
  function feeDisplay(row: LedgerRow) {
    if (row.type !== 'fee') return null;
    const ex = row.feeExTax ?? 0;
    const tax = row.taxAmount ?? 0;
    return taxDisplay === 'included' ? ex + tax : ex;
  }
  function taxDisplayAmount(row: LedgerRow) {
    if (row.type !== 'fee') return null;
    return row.taxAmount ?? 0;
  }
  function netImpact(row: LedgerRow) {
    if (row.type === 'fee') {
      const ex = row.feeExTax ?? 0;
      const tax = row.taxAmount ?? 0;
      return ex + tax;
    }
    if (row.type === 'payment' || row.type === 'refund' || row.type === 'dispute' || row.type === 'dispute_reversal') {
      return row.gross ?? 0;
    }
    if (row.type === 'reserve_hold') return -200;
    if (row.type === 'reserve_release') return 200;
    return 0;
  }
  return <div className="space-y-5" data-id="element-13940">
      {/* Recent activity timeline - moved to top */}
      <Card title={t('ui.TransactionFinancialBreakdown.attrs.recent_activity')} subtitle="Transaction timeline with all key events and amounts" data-id="element-13941">
        <div className="space-y-4" data-id="element-13942">
          {viewModel.timelineItems && viewModel.timelineItems.length > 0 ? viewModel.timelineItems.map((t, idx) => {
          const isLast = idx === viewModel.timelineItems.length - 1;
          return <div key={idx} className="relative" data-id="element-13943">
                  {!isLast && <div className="absolute left-3 top-8 bottom-0 w-0.5 bg-slate-200" data-id="element-13944" />}
                  <div className="flex gap-4" data-id="element-13945">
                    <div className="relative flex-shrink-0" data-id="element-13946">
                      <div className={`h-6 w-6 rounded-full flex items-center justify-center ring-4 ring-white ${t.type === 'positive' ? 'bg-[#FFF4B8]' : t.type === 'negative' ? 'bg-red-100' : 'bg-slate-100'}`} data-id="element-13947">
                        <div className={`h-2.5 w-2.5 rounded-full ${t.type === 'positive' ? 'bg-[#FFC70A]' : t.type === 'negative' ? 'bg-red-600' : 'bg-slate-400'}`} data-id="element-13948" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0 pb-4" data-id="element-13949">
                      <div className="flex items-start justify-between gap-3" data-id="element-13950">
                        <div className="flex-1" data-id="element-13951">
                          <div className="text-sm font-medium text-slate-900" data-id="element-13952">
                            {t.label}
                          </div>
                          <div className="mt-0.5 text-xs text-slate-600" data-id="element-13953">
                            {t.at}
                          </div>
                        </div>
                        {t.amount !== null && <div className="flex-shrink-0" data-id="element-13954">
                            <div className={`text-sm font-semibold ${t.type === 'positive' ? 'text-[#B38A00]' : t.type === 'negative' ? 'text-red-700' : 'text-slate-700'}`} data-id="element-13955">
                              {t.type === 'positive' ? '+' : t.type === 'negative' ? '−' : ''}
                              <Money value={t.amount} currency={currency} data-id="element-13956" />
                            </div>
                          </div>}
                      </div>
                    </div>
                  </div>
                </div>;
        }) : <div className="text-sm text-slate-600" data-id="element-13957">
              {t('ui.TransactionFinancialBreakdown.no_activity_recorded')}
            </div>}
        </div>
      </Card>

      {/* Financial summary */}
      <Card title={t('ui.TransactionFinancialBreakdown.attrs.financial_summary')} subtitle={`As of ${asOf}. Values reflect posted ledger entries; pending rows may change totals later.`} right={<div className="flex flex-wrap justify-end gap-2" data-id="element-13959">
            {flags.map(f => <Badge key={f.label} tone={f.tone} data-id="element-13960">
                {f.label}
              </Badge>)}
          </div>} data-id="element-13958">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-id="element-13961">
          {/* Balance impact */}
          <div className="rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200" data-id="element-13962">
            <div className="flex items-center justify-between gap-3" data-id="element-13963">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-600" data-id="element-13964">
                {t('ui.TransactionFinancialBreakdown.impact_on_merchant_balance')}
              </div>
              <div className="flex items-center gap-2" data-id="element-13965">
                <span className="text-xs text-slate-600" data-id="element-13966">
                  {t('ui.TransactionFinancialBreakdown.tax_display')}
                </span>
                <div className="inline-flex rounded-full bg-white p-1 ring-1 ring-slate-200" data-id="element-13967">
                  <button onClick={() => setTaxDisplay('included')} className={`rounded-full px-3 py-1 text-xs font-medium ${taxDisplay === 'included' ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-50'}`} data-id="element-13968">
                    {t('ui.TransactionFinancialBreakdown.included')}
                  </button>
                  <button onClick={() => setTaxDisplay('separate')} className={`rounded-full px-3 py-1 text-xs font-medium ${taxDisplay === 'separate' ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-50'}`} data-id="element-13969">
                    {t('ui.TransactionFinancialBreakdown.separate')}
                  </button>
                </div>
              </div>
            </div>
            <div className="mt-3 divide-y divide-slate-200" data-id="element-13970">
              <Row label={t('ui.TransactionFinancialBreakdown.attrs.gross_impact')} hint="Customer-facing amounts (payments/refunds/disputes)." value={<DeltaMoney value={computed.grossImpact} currency={currency} data-id="element-13972" />} data-id="element-13971" />

              {taxDisplay === 'included' ? <>
                  <Row label={t('ui.TransactionFinancialBreakdown.attrs.fees_posted_incl_tax_where_applicable')} hint="Includes tax for taxable fee components." value={<DeltaMoney value={computed.feesPostedDisplay} currency={currency} data-id="element-13974" />} data-id="element-13973" />
                </> : <>
                  <Row label={t('ui.TransactionFinancialBreakdown.attrs.fees_posted_ex_tax')} hint="Fee amounts excluding tax." value={<DeltaMoney value={computed.feesPostedEx} currency={currency} data-id="element-13976" />} data-id="element-13975" />

                  <Row label={t('ui.TransactionFinancialBreakdown.attrs.tax_on_fees_posted')} hint="Tax booked on fee components (some fee types may have 0 tax)." value={<DeltaMoney value={computed.taxPosted} currency={currency} data-id="element-13978" />} data-id="element-13977" />
                </>}
              <Row label={t('ui.TransactionFinancialBreakdown.attrs.reserve_held')} hint={computed.nextReserveRelease ? `Scheduled release on ${computed.nextReserveRelease}.` : 'No reserve active.'} value={computed.reserveHeld > 0.0001 ? <DeltaMoney value={-computed.reserveHeld} currency={currency} data-id="element-13980" /> : <span className="text-sm text-slate-500" data-id="element-13981">—</span>} data-id="element-13979" />

              <Row label={t('ui.TransactionFinancialBreakdown.attrs.net_impact_posted_so_far')} hint="Sum of posted ledger entries (truth)." value={<DeltaMoney value={computed.netPosted} currency={currency} data-id="element-13983" />} data-id="element-13982" />
            </div>
          </div>

          {/* Cash movement */}
          <div className="rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200" data-id="element-13984">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-600" data-id="element-13985">
              {t('ui.TransactionFinancialBreakdown.cash_movement')}
            </div>
            <div className="mt-3 divide-y divide-slate-200" data-id="element-13986">
              <Row label={t('ui.TransactionFinancialBreakdown.attrs.paid_out')} hint="Amount already included in completed payouts." value={<DeltaMoney value={computed.paidOut} currency={currency} data-id="element-13988" />} data-id="element-13987" />

              <Row label={t('ui.TransactionFinancialBreakdown.attrs.scheduled_in_upcoming_payouts')} hint="Sum of amounts mapped to scheduled payout buckets." value={<DeltaMoney value={computed.scheduledPayoutTotal} currency={currency} data-id="element-13990" />} data-id="element-13989" />

              <Row label={t('ui.TransactionFinancialBreakdown.attrs.held_in_reserve')} hint="Funds temporarily withheld per reserve policy." value={computed.reserveHeld > 0.0001 ? <DeltaMoney value={-computed.reserveHeld} currency={currency} data-id="element-13992" /> : <span className="text-sm text-slate-500" data-id="element-13993">—</span>} data-id="element-13991" />

              <Row label={t('ui.TransactionFinancialBreakdown.attrs.next_scheduled_payout')} hint="Where the next part of this transaction will land." value={viewModel.payoutBuckets.length ? <span className="text-sm text-slate-900" data-id="element-13995">
                      {viewModel.payoutBuckets[0].id} •{' '}
                      {viewModel.payoutBuckets[0].date}
                    </span> : <span className="text-sm text-slate-500" data-id="element-13996">—</span>} data-id="element-13994" />
            </div>
          </div>
        </div>
      </Card>

      {/* Unified ledger table */}
      <Table data-id="element-13997">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-5 py-4" data-id="element-13998">
          <div data-id="element-13999">
            <div className="text-sm font-semibold text-slate-900" data-id="element-14000">
              {t('ui.TransactionFinancialBreakdown.ledger_for_this_transaction')}
            </div>
            <div className="mt-1 text-xs text-slate-600" data-id="element-14001">
              {t('ui.TransactionFinancialBreakdown.every_posting_that_affects_the_merchant')}
            </div>
          </div>
          <div className="flex items-center gap-2" data-id="element-14002">
            <button className="rounded-xl bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm ring-1 ring-slate-200 hover:bg-slate-50" data-id="element-14003">
              {t('ui.TransactionFinancialBreakdown.export_csv')}
            </button>
          </div>
        </div>
        <div className="border-b border-slate-200 bg-slate-50 px-5 py-3" data-id="element-14004">
          <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-14005">
            <div className="text-xs text-slate-600" data-id="element-14006">
              {t('ui.TransactionFinancialBreakdown.fee_tax_display')}{' '}
              <span className="font-semibold text-slate-900" data-id="element-14007">
                {taxDisplay === 'included' ? 'Included in fee' : 'Shown separately'}
              </span>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-14008">
          <table className="min-w-full" data-id="element-14009">
            <thead className="bg-white" data-id="element-14010">
              <tr className="border-b border-slate-200" data-id="element-14011">
                <Th data-id="element-14012">{t('ui.TransactionFinancialBreakdown.posting')}</Th>
                <Th align="right" data-id="element-14013">
                  {t('ui.TransactionFinancialBreakdown.gross')}
                </Th>
                <Th align="right" data-id="element-14014">{t('labels.fee')}</Th>
                {taxDisplay === 'separate' && <Th align="right" data-id="element-14015">{t('labels.tax')}</Th>}
                <Th align="right" data-id="element-14016">
                  {t('ui.TransactionFinancialBreakdown.net_impact')}
                </Th>
                <Th data-id="element-14017">{t('labels.status')}</Th>
                <Th data-id="element-14018">{t('ui.TransactionFinancialBreakdown.effective')}</Th>
                <Th data-id="element-14019">{t('ui.TransactionFinancialBreakdown.settlement')}</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200" data-id="element-14020">
              {ledger.map(r => {
              const feeShown = feeDisplay(r);
              const taxShown = taxDisplayAmount(r);
              const net = getRowNetImpact(r);
              const settlement = r.settlement;
              const settlementUi = settlement?.kind === 'payout' ? <div className="space-y-1" data-id="element-14021">
                      <div className="text-sm font-medium text-slate-900" data-id="element-14022">
                        {settlement.id}
                      </div>
                      <div className="text-xs text-slate-600" data-id="element-14023">
                        {settlement.date} • {settlement.status}
                      </div>
                    </div> : settlement?.kind === 'reserve' ? <div className="space-y-1" data-id="element-14024">
                      <div className="text-sm font-medium text-slate-900" data-id="element-14025">
                        {t('ui.settlementUi.reserve')} {settlement.id}
                      </div>
                      <div className="text-xs text-slate-600" data-id="element-14026">
                        {t('ui.settlementUi.releases')}{' '}
                        {fmtDate(settlement.releaseDate || '')}
                      </div>
                    </div> : <span className="text-slate-500" data-id="element-14027">
                      {t('ui.settlementUi.not_yet_included')}
                    </span>;
              return <tr key={r.id} className="hover:bg-slate-50" data-id="element-14028">
                    <Td data-id="element-14029">
                      <div className="font-medium text-slate-900" data-id="element-14030">
                        {r.posting}
                      </div>
                      <div className="mt-1 text-xs text-slate-500" data-id="element-14031">{r.id}</div>
                    </Td>
                    <Td align="right" data-id="element-14032">
                      {r.gross == null ? <span className="text-slate-500" data-id="element-14033">—</span> : <DeltaMoney value={r.gross} currency={currency} data-id="element-14034" />}
                    </Td>
                    <Td align="right" data-id="element-14035">
                      {r.type !== 'fee' ? <span className="text-slate-500" data-id="element-14036">—</span> : <div className="flex items-center justify-end gap-2" data-id="element-14037">
                          <DeltaMoney value={feeShown!} currency={currency} data-id="element-14038" />
                          {r.feeBreakdown && <button onClick={() => setDrawer({
                      open: true,
                      ledgerId: r.id
                    })} className="text-xs text-blue-600 hover:text-blue-700 hover:underline" title={t('ui.TransactionFinancialBreakdown.attrs.view_fee_breakdown')} data-id="element-14039">
                              {t('ui.TransactionFinancialBreakdown.details')}
                            </button>}
                        </div>}
                    </Td>
                    {taxDisplay === 'separate' && <Td align="right" data-id="element-14040">
                        {r.type !== 'fee' ? <span className="text-slate-500" data-id="element-14041">—</span> : taxShown === 0 ? <span className="text-slate-500" data-id="element-14042">0.00</span> : <DeltaMoney value={taxShown!} currency={currency} data-id="element-14043" />}
                      </Td>}
                    <Td align="right" data-id="element-14044">
                      <DeltaMoney value={net} currency={currency} data-id="element-14045" />
                    </Td>
                    <Td data-id="element-14046">
                      {r.status === 'Posted' ? <Badge tone="emerald" data-id="element-14047">
                          {t('ui.TransactionFinancialBreakdown.posted')}
                        </Badge> : r.status === 'Pending' ? <Badge tone="amber" data-id="element-14048">{t('status.pending')}</Badge> : <Badge tone="blue" data-id="element-14049">
                          {t('ui.TransactionFinancialBreakdown.scheduled')}
                        </Badge>}
                    </Td>
                    <Td data-id="element-14050">
                      <div className="text-sm text-slate-800" data-id="element-14051">
                        {r.effectiveAt}
                      </div>
                    </Td>
                    <Td data-id="element-14052">{settlementUi}</Td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>
      </Table>

      {/* Payout / transfer story */}
      <Card title={t('ui.TransactionFinancialBreakdown.attrs.payout_transfer_story')} subtitle="Explains where money lands: payouts vs reserve, and how asynchronous fees/taxes affect reconciliation." data-id="element-14053">
        <div className="space-y-3" data-id="element-14054">
          {viewModel.payoutBuckets && viewModel.payoutBuckets.length > 0 ? viewModel.payoutBuckets.map(p => <div key={p.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200" data-id="element-14055">
                <div data-id="element-14056">
                  <div className="text-sm font-semibold text-slate-900" data-id="element-14057">
                    {p.id}
                  </div>
                  <div className="mt-1 text-xs text-slate-600" data-id="element-14058">
                    {p.date} • {p.status} • {p.rows.length}{' '}
                    {t('ui.TransactionFinancialBreakdown.postings')}
                  </div>
                </div>
                <div className="text-sm font-semibold" data-id="element-14059">
                  <DeltaMoney value={p.total} currency={currency} data-id="element-14060" />
                </div>
              </div>) : <div className="text-sm text-slate-600" data-id="element-14061">
              {t('ui.TransactionFinancialBreakdown.no_scheduled_payouts')}
            </div>}
          {viewModel.reserveBucket && <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-violet-50 p-4 ring-1 ring-violet-200" data-id="element-14062">
              <div data-id="element-14063">
                <div className="text-sm font-semibold text-violet-900" data-id="element-14064">
                  {t('ui.settlementUi.reserve')} {viewModel.reserveBucket.id}
                </div>
                <div className="mt-1 text-xs text-violet-900/80" data-id="element-14065">
                  {t('ui.TransactionFinancialBreakdown.held_now_releases')}{' '}
                  {viewModel.reserveBucket.releaseAt || '—'}
                </div>
              </div>
              <div className="text-sm font-semibold text-violet-900" data-id="element-14066">
                <DeltaMoney value={-viewModel.reserveBucket.heldNow} currency={currency} data-id="element-14067" />
              </div>
            </div>}
        </div>
      </Card>

      {/* Fee breakdown drawer */}
      <Drawer open={drawer.open} onClose={() => setDrawer({
      open: false,
      ledgerId: null
    })} title={selected ? `Fee breakdown — ${selected.posting}` : 'Fee breakdown'} data-id="element-14068">
        {!selected || !selected.feeBreakdown ? <div className="text-sm text-slate-600" data-id="element-14069">
            {t('ui.TransactionFinancialBreakdown.no_fee_details_available')}
          </div> : <div className="space-y-4" data-id="element-14070">
            <div className="rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200" data-id="element-14071">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-600" data-id="element-14072">
                {t('ui.TransactionFinancialBreakdown.jurisdiction')}
              </div>
              <div className="mt-2 text-sm font-semibold text-slate-900" data-id="element-14073">
                {selected.feeBreakdown.jurisdiction}
              </div>
              <div className="mt-2 text-xs text-slate-600" data-id="element-14074">
                {t('ui.TransactionFinancialBreakdown.display_mode')}{' '}
                <span className="font-semibold text-slate-900" data-id="element-14075">
                  {taxDisplay === 'included' ? 'Tax included in fee' : 'Tax shown separately'}
                </span>
              </div>
            </div>

            <div className="rounded-xl bg-white p-4 ring-1 ring-slate-200" data-id="element-14076">
              <div className="text-sm font-semibold text-slate-900" data-id="element-14077">
                {t('ui.TransactionFinancialBreakdown.components')}
              </div>
              <div className="mt-3 divide-y divide-slate-200" data-id="element-14078">
                {selected.feeBreakdown.items.map((it, idx) => <div key={idx} className="grid grid-cols-3 items-center gap-3 py-2" data-id="element-14079">
                    <div className="col-span-1 text-sm text-slate-700" data-id="element-14080">
                      {it.label}
                    </div>
                    <div className="col-span-1 text-right text-sm font-medium" data-id="element-14081">
                      <DeltaMoney value={it.amountExTax} currency={currency} data-id="element-14082" />
                    </div>
                    <div className="col-span-1 text-right text-sm font-medium" data-id="element-14083">
                      {it.taxAmount === 0 ? <span className="text-slate-500" data-id="element-14084">0.00</span> : <DeltaMoney value={it.taxAmount} currency={currency} data-id="element-14085" />}
                    </div>
                    <div className="col-span-3 -mt-1 text-xs text-slate-500" data-id="element-14086">
                      {t('ui.TransactionFinancialBreakdown.ex_tax_tax')}
                    </div>
                  </div>)}
              </div>
            </div>

            <div className="rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200" data-id="element-14087">
              <div className="flex items-center justify-between" data-id="element-14088">
                <div className="text-sm font-semibold text-slate-900" data-id="element-14089">
                  {t('ui.TransactionFinancialBreakdown.totals')}
                </div>
                <Badge tone={selected.status === 'Posted' ? 'emerald' : selected.status === 'Pending' ? 'amber' : 'blue'} data-id="element-14090">
                  {selected.status}
                </Badge>
              </div>
              <div className="mt-3 divide-y divide-slate-200" data-id="element-14091">
                <Row label={t('ui.TransactionFinancialBreakdown.attrs.fee_ex_tax')} value={<DeltaMoney value={selected.feeExTax ?? 0} currency={currency} data-id="element-14093" />} data-id="element-14092" />

                <Row label={t('ui.TransactionFinancialBreakdown.attrs.tax_on_fee')} value={<DeltaMoney value={selected.taxAmount ?? 0} currency={currency} data-id="element-14095" />} data-id="element-14094" />

                <Row label={t('ui.TransactionFinancialBreakdown.attrs.total_fee_inc_tax')} value={<DeltaMoney value={(selected.feeExTax ?? 0) + (selected.taxAmount ?? 0)} currency={currency} data-id="element-14097" />} data-id="element-14096" />
              </div>
            </div>
          </div>}
      </Drawer>
    </div>;
}